import {useCallback, useRef} from 'react';

export type ShiftSelectId = string | number;

export const isShiftClick = (event: {nativeEvent: Event}) =>
  Boolean((event.nativeEvent as MouseEvent).shiftKey);

interface ShiftSelectParams<T extends ShiftSelectId> {
  id: T;
  visibleIds: T[];
  selectedIds: T[];
  shiftKey?: boolean;
}

const useShiftSelect = <T extends ShiftSelectId>() => {
  const anchorRef = useRef<T | null>(null);

  return useCallback(
    ({id, visibleIds, selectedIds, shiftKey}: ShiftSelectParams<T>): T[] => {
      const anchorId = selectedIds.length === 0 ? null : anchorRef.current;
      const shouldSelect = !selectedIds.includes(id);
      anchorRef.current = id;

      const anchorIndex = anchorId === null ? -1 : visibleIds.indexOf(anchorId);
      const targetIndex = visibleIds.indexOf(id);

      if (!shiftKey || anchorIndex === -1 || targetIndex === -1) {
        if (shouldSelect) {
          return [...selectedIds, id];
        }
        return selectedIds.filter((i) => i !== id);
      }

      const rangeIds = visibleIds.slice(
        Math.min(anchorIndex, targetIndex),
        Math.max(anchorIndex, targetIndex) + 1
      );

      if (shouldSelect) {
        return [
          ...selectedIds,
          ...rangeIds.filter((i) => !selectedIds.includes(i)),
        ];
      }

      return selectedIds.filter((i) => !rangeIds.includes(i));
    },
    []
  );
};

export default useShiftSelect;
